import React from 'react';
import { IconButton } from '@material-ui/core';
import '../style/NetWorthChartDetail.css';

const PLOT = { width: 100, height: 100 };

class NetWorthChartDetail extends React.Component {
  constructor(props) {
    super(props);
    const chartData = this.getChartData(props.snapshots);
    const lastIndex = chartData ? chartData.points.length - 1 : null;
    this.state = {
      activeIndex: lastIndex
    };
    this.overlayRef = React.createRef();
    this.plotRef = React.createRef();
  }

  componentDidMount() {
    document.body.classList.add('NetWorthChartDetail-open');
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.attachTouchListeners();
    this.enterFullscreenAndLandscape();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.snapshots !== this.props.snapshots ||
      prevProps.excludeHouseEquity !== this.props.excludeHouseEquity
    ) {
      const chartData = this.getChartData(this.props.snapshots);
      const lastIndex = chartData ? chartData.points.length - 1 : null;
      this.setState({ activeIndex: lastIndex });
    }
  }

  componentWillUnmount() {
    this.detachTouchListeners();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.exitFullscreenAndLandscape();
    document.body.classList.remove('NetWorthChartDetail-open');
  }

  handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      this.props.onClose();
    }
  }

  attachTouchListeners = () => {
    const plot = this.plotRef.current;
    if (!plot) return;
    plot.addEventListener('touchmove', this.handleChartTouchMoveNative, { passive: false });
  }

  detachTouchListeners = () => {
    const plot = this.plotRef.current;
    if (!plot) return;
    plot.removeEventListener('touchmove', this.handleChartTouchMoveNative);
  }

  handleChartTouchMoveNative = (e) => {
    e.preventDefault();
    if (e.touches.length > 0) {
      this.setActiveIndexFromClientX(e.touches[0].clientX);
    }
  }

  enterFullscreenAndLandscape = async () => {
    try {
      const el = this.overlayRef.current;
      if (el && el.requestFullscreen) {
        await el.requestFullscreen();
      }
    } catch (err) {
      // Fullscreen not available on some mobile browsers
    }

    try {
      if (window.screen.orientation && window.screen.orientation.lock) {
        await window.screen.orientation.lock('landscape');
      }
    } catch (err) {
      // Orientation lock requires fullscreen/PWA on some browsers
    }
  }

  exitFullscreenAndLandscape = async () => {
    try {
      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    } catch (err) {
      // ignore
    }

    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      // ignore
    }
  }

  handleClose = () => {
    this.exitFullscreenAndLandscape().then(() => {
      this.props.onClose();
    });
  }

  formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate = (dateString) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Denver'
    });
  }

  formatAxisDate = (dateString) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/Denver' });
    const year = date.toLocaleDateString('en-US', { year: '2-digit', timeZone: 'America/Denver' });
    return `${month} '${year}`;
  }

  displayedAmount = (snapshot) => {
    const amount = Number(snapshot.amount) || 0;
    if (!this.props.excludeHouseEquity) {
      return amount;
    }
    return amount - (Number(snapshot.house_equity) || 0);
  }

  getChartData = (snapshots) => {
    if (!snapshots || snapshots.length < 2) {
      return null;
    }

    const sortedSnapshots = [...snapshots].sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });

    const amounts = sortedSnapshots.map(s => this.displayedAmount(s));
    const minAmount = Math.min(...amounts);
    const maxAmount = Math.max(...amounts);
    const amountRange = maxAmount - minAmount || 1;

    const points = sortedSnapshots.map((snapshot, index) => {
      const x = (index / (sortedSnapshots.length - 1)) * PLOT.width;
      const amount = this.displayedAmount(snapshot);
      const normalizedAmount = (amount - minAmount) / amountRange;
      const y = PLOT.height - (normalizedAmount * PLOT.height);
      return { x, y, amount, date: snapshot.created_at, index };
    });

    const pathData = points.map((point, i) => {
      return `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ');

    const areaPath = `${pathData} L ${points[points.length - 1].x} ${PLOT.height} L ${points[0].x} ${PLOT.height} Z`;

    const maxLabels = Math.min(6, sortedSnapshots.length);
    const labelIndices = [];

    if (sortedSnapshots.length <= maxLabels) {
      for (let i = 0; i < sortedSnapshots.length; i++) {
        labelIndices.push(i);
      }
    } else {
      for (let i = 0; i < maxLabels; i++) {
        const index = Math.floor((i / (maxLabels - 1)) * (sortedSnapshots.length - 1));
        labelIndices.push(index);
      }
    }

    return {
      points,
      pathData,
      areaPath,
      minAmount,
      maxAmount,
      labelIndices,
      pointCount: sortedSnapshots.length
    };
  }

  indexFromClientX = (clientX) => {
    const plot = this.plotRef.current;
    const chartData = this.getChartData(this.props.snapshots);
    if (!plot || !chartData) {
      return null;
    }

    const rect = plot.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (chartData.points.length - 1));
  }

  setActiveIndexFromClientX = (clientX) => {
    const index = this.indexFromClientX(clientX);
    if (index !== null) {
      this.setState({ activeIndex: index });
    }
  }

  handleChartTouchStart = (e) => {
    if (e.touches.length > 0) {
      this.setActiveIndexFromClientX(e.touches[0].clientX);
    }
  }

  handleChartMouseMove = (e) => {
    this.setActiveIndexFromClientX(e.clientX);
  }

  renderPlot = (chartData) => {
    const { activeIndex } = this.state;
    const { points, pathData, areaPath, labelIndices } = chartData;
    const activePoint = activeIndex !== null ? points[activeIndex] : null;

    return (
      <svg
        viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
        className="NetWorthChartDetail-svg"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="netWorthDetailGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#764ba2" />
          </linearGradient>
        </defs>

        <path d={areaPath} fill="url(#netWorthDetailGradient)" opacity="0.2" />

        <path
          d={pathData}
          fill="none"
          stroke="#667eea"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {activePoint && (
          <line
            x1={activePoint.x}
            y1="0"
            x2={activePoint.x}
            y2={PLOT.height}
            stroke="#667eea"
            strokeWidth="0.5"
            strokeDasharray="2 2"
            opacity="0.7"
          />
        )}

        {points.map((point, index) => {
          const isLabelPoint = labelIndices.includes(index);
          const isActive = activeIndex === index;
          if (!isActive && !isLabelPoint) return null;
          return (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r={isActive ? 2 : 1.2}
              fill={isActive ? '#764ba2' : '#667eea'}
              opacity={isActive ? 1 : 0.5}
              stroke={isActive ? '#fff' : 'none'}
              strokeWidth="0.4"
            />
          );
        })}
      </svg>
    );
  }

  render() {
    const { snapshots } = this.props;
    const chartData = this.getChartData(snapshots);

    if (!chartData) {
      return null;
    }

    const { activeIndex } = this.state;
    const { points, minAmount, maxAmount, labelIndices, pointCount } = chartData;
    const activePoint = activeIndex !== null ? points[activeIndex] : null;

    return (
      <div
        className="NetWorthChartDetail-overlay"
        ref={this.overlayRef}
        role="dialog"
        aria-modal="true"
        aria-label="Net worth detailed chart"
      >
        <div className="NetWorthChartDetail-topbar">
          {activePoint && (
            <div className="NetWorthChartDetail-scrubber-value">
              <span className="NetWorthChartDetail-scrubber-amount">
                {this.formatCurrency(activePoint.amount)}
              </span>
              <span className="NetWorthChartDetail-scrubber-date">
                {this.formatDate(activePoint.date)}
              </span>
            </div>
          )}
          <IconButton
            className="NetWorthChartDetail-close-button"
            onClick={this.handleClose}
            aria-label="Close chart"
          >
            ×
          </IconButton>
        </div>

        <div className="NetWorthChartDetail-body">
          <div className="NetWorthChartDetail-y-axis" aria-hidden="true">
            <span className="NetWorthChartDetail-y-label">{this.formatCurrency(maxAmount)}</span>
            <span className="NetWorthChartDetail-y-label">{this.formatCurrency(minAmount)}</span>
          </div>

          <div className="NetWorthChartDetail-main">
            <div
              className="NetWorthChartDetail-plot"
              ref={this.plotRef}
              onTouchStart={this.handleChartTouchStart}
              onMouseMove={this.handleChartMouseMove}
            >
              {this.renderPlot(chartData)}
            </div>

            <div className="NetWorthChartDetail-x-axis" aria-hidden="true">
              {labelIndices.map((labelIndex) => {
                const point = points[labelIndex];
                const leftPercent = (labelIndex / (pointCount - 1)) * 100;
                return (
                  <span
                    key={labelIndex}
                    className="NetWorthChartDetail-x-label"
                    style={{ left: `${leftPercent}%` }}
                  >
                    {this.formatAxisDate(point.date)}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default NetWorthChartDetail;
